import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Mail, Send, MessageCircle, CheckCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        throw new Error('request_failed');
      }
      setIsSubmitted(true);
      setFormData({ name: '', company: '', email: '', phone: '', message: '' });
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (err) {
      setError(t.contact.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: '#E5E7EB', color: '#1F4FD8' }}>
            {t.contact.badge}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            {t.contact.title}
            <span style={{ color: '#1F4FD8' }}>{t.contact.titleHighlight}</span>
          </h2>
          <p className="text-xl text-slate-600">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <div className="space-y-6 mb-10">
              <Card className="bg-gray-50 border-0">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E5E7EB' }}>
                    <MapPin className="w-6 h-6" style={{ color: '#1F4FD8' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{t.contact.address}</h3>
                    <p className="text-slate-600 font-semibold">{companyInfo.name}</p>
                    <p className="text-slate-600">{companyInfo.address}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-50 border-0">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E5E7EB' }}>
                    <Phone className="w-6 h-6" style={{ color: '#1F4FD8' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{t.contact.whatsapp}</h3>
                    <a
                      href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-colors hover:opacity-80"
                      style={{ color: '#1F4FD8' }}
                    >
                      {companyInfo.whatsapp}
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-50 border-0">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E5E7EB' }}>
                    <Mail className="w-6 h-6" style={{ color: '#1F4FD8' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{t.contact.email}</h3>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="transition-colors hover:opacity-80"
                      style={{ color: '#1F4FD8' }}
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* WhatsApp CTA */}
            <a
              href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Bonjour, je souhaite obtenir des informations sur les capteurs SMARTMICRO.`}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="p-6 rounded-2xl text-white transition-all shadow-lg hover:shadow-xl" style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', boxShadow: '0 10px 30px rgba(37, 211, 102, 0.25)' }}>
                <div className="flex items-center gap-4">
                  <MessageCircle className="w-10 h-10" />
                  <div>
                    <p className="font-bold text-lg">{t.contact.whatsappCta}</p>
                  </div>
                </div>
              </div>
            </a>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="bg-gray-50 border-0 shadow-xl" style={{ boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)' }}>
              <CardContent className="p-8">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#E5E7EB' }}>
                      <CheckCircle className="w-8 h-8" style={{ color: '#1F4FD8' }} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{t.contact.form.success}</h3>
                    <p className="text-slate-600">{t.contact.form.successText}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t.contact.form.fullName}
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          data-testid="contact-name-input"
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                          placeholder={t.contact.form.fullNamePlaceholder}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t.contact.form.company}
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          data-testid="contact-company-input"
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                          placeholder={t.contact.form.companyPlaceholder}
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t.contact.form.email}
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          data-testid="contact-email-input"
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                          placeholder={t.contact.form.emailPlaceholder}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t.contact.form.phone}
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          data-testid="contact-phone-input"
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                          placeholder={t.contact.form.phonePlaceholder}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t.contact.form.message}
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        data-testid="contact-message-input"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all resize-none"
                        placeholder={t.contact.form.messagePlaceholder}
                      />
                    </div>
                    {error && (
                      <p className="text-sm text-red-600" data-testid="contact-error-message">{error}</p>
                    )}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-button"
                      className="w-full text-white py-6 text-lg transition-all disabled:opacity-70"
                      style={{ backgroundColor: '#1F4FD8', boxShadow: '0 10px 25px rgba(31, 79, 216, 0.25)' }}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t.contact.form.submitting}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          {t.contact.form.submit}
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
